import { reactive, readonly } from "vue";
import { ASSISTENT_API_URL } from "@/config/backend"
import axios from "axios";
import authModule from "../auth";
import devicesModule from "../devices";
import useAlert from "@/hooks/useAlert";
import { useTranslation } from "@/locales";

const { getLanguage } = useTranslation();
const { showAlertByKey } = useAlert();

const state = reactive({
    answerCategories: [],
    currentCategory: null,
    currentAnswersByCategory: [],
    callhandlingProfiles: [],
    processing: false,
    globalProcessing: false,
});

const actions = {
    readCategories: async () => {
        if (devicesModule.getters.isDeviceSelected() && (state.answerCategories == null || state.answerCategories.length == 0)) {
            const url = ASSISTENT_API_URL + "answers/" + devicesModule.state.currentDeviceSerialId + "/categories/" + getLanguage();

            state.processing = true;
            return axios
                .get(
                    url,
                    {
                        headers: authModule.getters.getApiHeaders()
                    }
                )
                .then((response) => {
                    if (response.data.errorCode !== null) {
                        showAlertByKey(response.data.errorCode);
                        state.answerCategories.splice(0);
                        state.processing = false;
                    } else {
                        state.answerCategories.splice(
                            0,
                            state.answerCategories,
                            ...Object.values(response.data.resultObject.categories));

                        actions.loadCategoryData();
                        actions.setFirstCategory();
                    }

                })
                .catch(() => {
                    state.processing = false;
                    showAlertByKey("error.unknown");
                    const errorMessage = new Error('UNKNOWN_ERROR');
                    throw errorMessage;
                });
        }
    },
    readAllData: async () => {
        await actions.readCategories();
        await actions.loadCategoryData();
        actions.setFirstCategory();

        actions.loadCallHandlingProfiles();
    },
    loadCategoryData: async () => {
        for (const cat of state.answerCategories) {
            if (state.currentAnswersByCategory[cat.id] == null) {
                await actions.refreshCategory(cat.id);
            }
        }
    },
    loadCallHandlingProfiles: async () => {
        const url =
            ASSISTENT_API_URL +
            "device/" +
            devicesModule.state.currentDeviceSerialId +
            "/callhandling/profiles";
        return axios
            .get(url, {
                headers: authModule.getters.getApiHeaders(),
            })
            .then((response) => {
                state.callhandlingProfiles = response.data.resultObject.profiles;
            })
            .catch(() => {
                showAlertByKey("error.unknown");
            });
    },
    setCategoryById: (categoryId) => {
        if (state === null || state.currentCategory === null || state.currentCategory.id === null || state.currentCategory.id !== categoryId) {
            const cat = actions.getCategory(categoryId);
            if (cat !== null) {
                actions.setCategory(cat);
            }
        }
    },
    getCategory: (categoryId) => {
        return state.answerCategories.find(item => item.id === String(categoryId));
    },
    setCategory: (category) => {
        if (state.currentCategory != null && state.currentCategory.id == category.id) {
            state.currentCategory = null;
            state.processing = false;
        } else if (state.currentAnswersByCategory[category.id] != null) {
            state.currentCategory = category;
            state.processing = false;
        } else {
            state.currentCategory = category;

            return actions.refreshCategory(category.id);
        }
    },
    setFirstCategory: () => {
        if (state.answerCategories.length == 1) {
            state.currentCategory = state.answerCategories[0];
        }
        else if (state.answerCategories.length > 1) {
            state.currentCategory = actions.getFirstCategoryWithAnswers();
        } else {
            state.currentCategory = null;
        }
    },
    refreshCategory: async (requestedCategoryId) => {
        const category = actions.getCategory(requestedCategoryId);
        if (category != null) {
            if (category.type === 'ANSWER') {
                const url = ASSISTENT_API_URL + "answers/" + devicesModule.state.currentDeviceSerialId + "/" + requestedCategoryId;

                state.processing = true;
                return axios
                    .get(
                        url,
                        {
                            headers: authModule.getters.getApiHeaders()
                        }
                    )
                    .then((response) => {
                        if (response.data.errorCode !== null) {
                            showAlertByKey(response.data.errorCode);
                        } else {
                            state.currentAnswersByCategory[requestedCategoryId] = response.data.resultObject.answerTypes;
                        }
                        state.processing = false;
                    })
                    .catch(() => {
                        state.processing = false;
                        const errorMessage = new Error('UNKNOWN_ERROR');
                        throw errorMessage;
                    });
            } else {
                const url = ASSISTENT_API_URL + "answers/options/" + devicesModule.state.currentDeviceSerialId + "/" + requestedCategoryId;

                state.processing = true;
                return axios
                    .get(
                        url,
                        {
                            headers: authModule.getters.getApiHeaders()
                        }
                    )
                    .then((response) => {
                        if (response.data.errorCode !== null) {
                            showAlertByKey(response.data.errorCode);
                        } else {
                            state.currentAnswersByCategory[requestedCategoryId] = response.data.resultObject.optionTypes;
                        }
                        state.processing = false;
                    })
                    .catch(() => {
                        state.processing = false;
                        const errorMessage = new Error('UNKNOWN_ERROR');
                        throw errorMessage;
                    });
            }
        }
    },
    getCurentAnswers: () => {
        if (state.currentCategory != null && state.currentCategory.id != null && state.currentAnswersByCategory[state.currentCategory.id] != null) {
            return state.currentAnswersByCategory[state.currentCategory.id];
        }
        return [];
    },
    getAllAnswers: () => {
        const allAnswers = Object.values(state.currentAnswersByCategory).flat();
        return allAnswers;
    },
    getAnswersByCategory: (category) => {
        if (state.currentAnswersByCategory[category.id] != null) {
            return state.currentAnswersByCategory[category.id];
        }
        return [];
    },
    getAnswerCountForCategory: (category) => {
        return state.currentAnswersByCategory[category.id] != null ? state.currentAnswersByCategory[category.id].length : 0;
    },
    getFirstCategoryWithAnswers: () => {
        for (const category of state.answerCategories) {
            if (actions.getAnswerCountForCategory(category) > 0) {
                return category;
            }
        }
        return null;
    },
    reset: async () => {
        state.globalProcessing = true;
        state.answerCategories = [];
        state.processing = false;
        state.currentCategory = null;
        state.currentAnswersByCategory = [];

        if (devicesModule.getters.isDeviceSelected() && authModule.getters.isCustomer()
            && !devicesModule.getters.isMdsDevice()) {
            actions.readAllData();
        }
        state.globalProcessing = false;
    },
    resetCallhandlingProfiles: async () => {
        state.globalProcessing = true;
        state.callhandlingProfiles = [];
        await actions.loadCallHandlingProfiles();
        state.globalProcessing = false;
    },
    getActionByTitle: (actionTitle) => {
        const actionList = Object.values(state.currentAnswersByCategory).flat();
        if (actionList === null) {
            return null;
        }
        const result = actionList.find(item => item.id === actionTitle);
        if (result === null) {
            return null;
        }
        return result;
    },
    isActionEditable: (actionTitle) => {
        const action = actions.getActionByTitle(actionTitle);
        if (action) {
            return action.editableByCustomer;
        } else {
            return false;
        }
    },
    updateAnswerSmsMode: (actionTitle, newMode) => {
        const action = actions.getActionByTitle(actionTitle);
        if (action) {
            action.smsEnabled = newMode;
        }
    },
    updateAnswerForwardingMode: (actionTitle, newMode) => {
        const action = actions.getActionByTitle(actionTitle);
        if (action) {
            action.forwardingEnabled = newMode;
        }
    },
    findCallHandlingProfileForAnswer: (answerId) => {
        return state.callhandlingProfiles.find(
            (p) =>
                p.usedByActions &&
                p.usedByActions.some((a) => a === answerId)
        ) || null;
    },
    isProcessing: () => {
        return state.processing || state.globalProcessing;
    }
}

const customerModule = {
    state: readonly(state),
    actions
}

export default customerModule;